import {
  AuthFailureReason,
  getAuthModule,
  isAuthSessionError,
  shouldForceLogout,
} from '@littlebird/auth-client';
import { redirect } from '@tanstack/react-router';

import { userStore } from '@/stores/user.store';

export interface AuthContext {
  /**
   * Check if user is authenticated (with hydration). Returns true if
   * authenticated; throws a redirect to /error/session-expired for a dead
   * session (revoked/expired refresh token).
   */
  checkAuthenticated: () => Promise<boolean>;

  /** Validate token, refresh if needed, throw redirect on failure */
  requireAuth: (location: {
    pathname: string;
    search: Record<string, unknown>;
  }) => Promise<void>;
}

export function createAuthContext(): AuthContext {
  return {
    async checkAuthenticated() {
      if (userStore.isAuthenticated) {
        return true;
      }

      try {
        const auth = getAuthModule();
        await auth.getAccessToken();
        if (auth.isAuthenticated() && !userStore.user) {
          await userStore.refresh();
        }
      } catch (error) {
        if (isAuthSessionError(error)) {
          // REFRESH_TOKEN_MISSING means that the user is logged out. Return false.
          if (error.reason === AuthFailureReason.REFRESH_TOKEN_MISSING) {
            return false;
          }
          // A dead session (revoked/expired token) needs the session-expired redirect
          if (shouldForceLogout(error.reason)) {
            throw redirect({
              to: '/error/session-expired',
              search: { reason: error.reason },
            });
          }
          throw error;
        }
        // Non-auth failures mean we could not verify - treat as logged out
        return false;
      }

      return userStore.isAuthenticated;
    },

    async requireAuth(location) {
      const isAuthenticated = await this.checkAuthenticated();

      if (!isAuthenticated) {
        const searchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(location.search)) {
          if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        }
        const searchStr = searchParams.toString();
        const hash = window.location.hash;
        const fullPath = `${location.pathname}${searchStr ? `?${searchStr}` : ''}${hash}`;

        throw redirect({
          to: '/login',
          search: { redirect: encodeURIComponent(fullPath) },
        });
      }
    },
  };
}
